import * as React from 'react';
import { graphql } from 'gatsby';
import { Helmet } from 'react-helmet';
import { Layout, OpenGraphHelmet } from '../components';
import { ContentfulFaqContainer, ContentfulContactUs, SiteConfig, ContentfulFeedbackContainer, ContentfulComparisonTable, ContentfulHeroFrame, ContentfulPlanContainer, ContentfulMobileBannerContainer, ContentfulCalculatorItem, ContentfulCreditOptionsSelector, ContentfulDownloadSoftwareContainer, ContentfulLegalTermsContainer } from '../interfaces';
import { FaqContainer } from '../../src/components/dynamic-components/faq-container';
import { ContactUs } from '../components/dynamic-components/contact-us/contact-us';
import { FeedbackCarousel } from '../components/dynamic-components/feedback-carousel/feedback-carousel';
import { BillingSectionController, BillingToggle } from '../components/dynamic-components/shared/billing-context/billing-period-toggle';
import { ComparisonTable } from '../components/dynamic-components/comparison-table/comparison-table';
import SingleLogoHeroFrame from '../components/dynamic-components/frames/hero-frame/single-logo-hero-frame';
import { useScrollToHash } from '../components/dynamic-components/shared/hooks/use-scroll-to-hash';
import { PlansCardsContainer } from '../components/dynamic-components/plans-cards-container/plans-cards-container';
import { CalculatorItem } from '../components/dynamic-components/calculator-item';
import { localizeLink } from '../utils';
import { Locale } from '../translations';
import { MobileBanners } from '../components/dynamic-components/mobile-banners/mobile-banners';
import { CONTENTS } from './frame-page-display/frame-page-display-properties';
import { fixContentfulAssetPath, } from '../utils';
import { CreditOptionsSelector } from '../components/dynamic-components/credit-options-selector/credit-options-selector';
import { DownloadSoftwareContainer } from '../components/dynamic-components/download-software-container';
import  LegalTermsContainer  from '../components/dynamic-components/legal-terms-container/legal-terms-container'; 

import './main-page.scss';

export type MainPageSectionsUnion = 
  ContentfulFaqContainer | ContentfulContactUs | ContentfulFeedbackContainer | ContentfulComparisonTable | ContentfulPlanContainer | ContentfulHeroFrame | ContentfulMobileBannerContainer | ContentfulCalculatorItem  | ContentfulCreditOptionsSelector | ContentfulDownloadSoftwareContainer | ContentfulLegalTermsContainer;

export interface ContentfulMainPage {
  metaTitle?: string;
  metaDescription?: string; 
  noIndex?: boolean;
  noFollow?: boolean;
  slug?: string;

  socialImage?: {
    file: {
      url: string;
    };
  };
  sections: MainPageSectionsUnion[];
}

export interface MainPageProps {
  pageContext: {
    id: string;
    englishCanonicalUrl: string;
    locale: Locale;
  };
  data: {
    contentfulMainPage: ContentfulMainPage | null;
    site: SiteConfig;
  };
}

export enum ContentfulTypes {
  ContentfulHeroFrame = 'ContentfulHeroFrame',
  ContentfulFaqContainer = 'ContentfulFaqContainer',
  ContentfulContactUs = "ContentfulContactUs",
  ContentfulFeedbackContainer = 'ContentfulFeedbacksContainer',
  ContentfulPlanCardsContainer = 'ContentfulPlanCardsContainer',
  ContentfulComparisonTable = 'ContentfulComparisonTable',
  ContentfulMobileBannerContainer = 'ContentfulMobileBannerContainer',
  ContentfulContent = 'ContentfulContent',
  ContentfulCalculatorItem = 'ContentfulCalculatorItem',
  ContentfulCreditOptionsSelector = 'ContentfulCreditOptionsSelector',
  ContentfulDownloadSoftwareContainer = 'ContentfulDownloadSoftwareContainer',
  ContentfulLegalTermsContainer = 'ContentfulLegalTermsContainer',
}

export function MainPage({ data, pageContext }: MainPageProps) {
  useScrollToHash();

  const slug = data.contentfulMainPage?.slug || '';
  const metaTitle = data.contentfulMainPage?.metaTitle;
  const metaDescription = data.contentfulMainPage?.metaDescription;
  const noIndex = data.contentfulMainPage?.noIndex;
  const noFollow = data.contentfulMainPage?.noFollow;
  const socialImage = data.contentfulMainPage?.socialImage;
  const sections = data.contentfulMainPage?.sections ?? [];
  const { locale, englishCanonicalUrl } = pageContext;
  const heroSection = sections.find(s => s.__typename === ContentfulTypes.ContentfulHeroFrame) as ContentfulHeroFrame;

  const slashSlug = slug.startsWith('/') ? slug : `/${slug}`;

  const planSection = sections.find((section) => section.__typename === ContentfulTypes.ContentfulPlanCardsContainer);

  const canonical = `${data.site.siteMetadata.siteUrl}${localizeLink(locale, slashSlug)}`;

  const robots = [
    noIndex ? 'noindex' : null,
    noFollow ? 'nofollow' : null,
  ]
  .filter(Boolean)
  .join(', ');

  let selectedSocialImage = '';

  if (socialImage) {
    selectedSocialImage = socialImage.file.url;
  } else if (heroSection && heroSection.backgroundImage) {
    selectedSocialImage = heroSection.backgroundImage.file.url;
  }
  
  selectedSocialImage = fixContentfulAssetPath(selectedSocialImage);

  return (
    <Layout
      title={metaTitle}
      description={metaDescription}
      englishCanonicalUrl={englishCanonicalUrl}
      notRenderHeaderAndFooter={false}
      isGated={false}
      locale={locale}
    >
      <Helmet>
        <html lang={locale} />
        <link rel="canonical" href={canonical} />
        {robots && <meta name="robots" content={robots} />}
      </Helmet>
      
      <OpenGraphHelmet
        title={metaTitle || ''}
        description={metaDescription || ''}
        socialImage={selectedSocialImage}
        url={canonical}
        type="website"
      />

      <BillingSectionController data={planSection?.billingPeriods || []}>
        {({ options }) => (
          <div className='main-page'>
            {sections.map((section, index) => {
              switch (section.__typename as string) {
                case ContentfulTypes.ContentfulHeroFrame:
                  return <SingleLogoHeroFrame key={index} {...section as ContentfulHeroFrame} />; 

                case ContentfulTypes.ContentfulFaqContainer:
                  return <FaqContainer key={index} {...section as ContentfulFaqContainer} />; 

                case ContentfulTypes.ContentfulContactUs:
                  return <ContactUs key={index} {...section as ContentfulContactUs} />;

                case ContentfulTypes.ContentfulPlanCardsContainer: 
                  const castedSection = section as ContentfulPlanContainer;
                  return (
                    <React.Fragment key={index}>
                      {castedSection.title && <h2>{castedSection.title.text}</h2>}
                      {options.length > 1 && <BillingToggle options={options} />}
                      <PlansCardsContainer {...(section as ContentfulPlanContainer)} />
                    </React.Fragment>
                  );
                  
                case ContentfulTypes.ContentfulFeedbackContainer:
                  return <FeedbackCarousel key={index} {...section as ContentfulFeedbackContainer} />;

                case ContentfulTypes.ContentfulComparisonTable:
                  return (
                    <ComparisonTable
                      key={index}
                      tableData={section as ContentfulComparisonTable} 
                      billingFormats={planSection?.billingPeriods || []}
                    />
                  );

                case ContentfulTypes.ContentfulMobileBannerContainer:
                  return (
                    <MobileBanners key={index} {...section as ContentfulMobileBannerContainer}/>
                  );
                  
                case ContentfulTypes.ContentfulContent:
                  const { Component, mapProps } = CONTENTS[section.__typename];
                  return <Component key={index} {...mapProps(section, { index })} />;

                case ContentfulTypes.ContentfulCalculatorItem:
                  return (
                    <CalculatorItem
                      key={index}
                      {...(section as ContentfulCalculatorItem)}
                    />
                  );

                case ContentfulTypes.ContentfulDownloadSoftwareContainer:
                  return <DownloadSoftwareContainer key={index} {...section as ContentfulDownloadSoftwareContainer} />; 
                                
                case ContentfulTypes.ContentfulCreditOptionsSelector:
                  return (
                    <CreditOptionsSelector key={index} {...section as ContentfulCreditOptionsSelector}/>
                  );
                
                case ContentfulTypes.ContentfulLegalTermsContainer:
                  return <LegalTermsContainer key={index} {...section as ContentfulLegalTermsContainer} />; 

                default:
                  return <></>;
              }
            })}
          </div>
        )}
      </BillingSectionController>
    </Layout>
  );
}

export default MainPage;

export const mainPageQuery = graphql`
    query mainPageQuery($slug: String!, $locale: String!) {
      contentfulMainPage(
      slug: { eq: $slug }, fields: { locale: { eq: $locale } }) {     
      contentful_id
      metaTitle
      metaDescription
      noIndex
      noFollow
      slug 
      socialImage { 
        file {
          url
        }
      }
      sections {
        ... on ContentfulCreditOptionsSelector {
          __typename
          contentful_id
          title {
            __typename
            contentful_id
            text
          }
          subtitle {
            __typename
            contentful_id
            text {
              raw
            }
          }
          showSubscriptionCost
          defaultSelection
          creditOptions {
            ... on ContentfulCreditOption {
              credits
              cost {
                ... on ContentfulPrice {
                  currency {
                    currency
                  }
                  price
                }
              }
              type
            } 
          }
        }
        ... on ContentfulMobileBannerContainer {
          __typename
          contentful_id
          banners {
            ... on ContentfulMobileBanner {
              __typename
              contentful_id
              title {
                __typename
                contentful_id
                text
              }
              description {
                __typename
                contentful_id
                text
              }
              image {
                url
              }
              url
            }
          }
        }
        ... on ContentfulContactUs {
          __typename
          contentful_id
          title {
            __typename
            contentful_id
            text
          }
          description {
            __typename
            contentful_id
            text {
              raw
            }
          }
          redirectLink {
            contentful_id
            slug
          }
          image {
            url
          }
        }
        ... on ContentfulFeedbacksContainer {
          __typename
          contentful_id
          title {
            __typename
            contentful_id
            text
          }
          items {
            ... on ContentfulFeedback {  
              __typename
              contentful_id
              logo {
                url
              }
              description {
                __typename
                contentful_id
                text {
                  raw
                }
              }
              name
              companyTitle
              companyName
              rating
            }
          }
          backgroundColor
        }
        ... on ContentfulComparisonTable {
          __typename
          contentful_id
          title {
            __typename
            contentful_id
            text
          }

          subtitle {
            __typename
            contentful_id
            text {
              raw
            }
          }

          footer {
            __typename
            contentful_id
            text {
              raw
            }
          }
          
          table {
            raw
          }
        }
        ... on ContentfulFaqContainer {
          __typename
          contentful_id
          title {
            __typename
            contentful_id
            text
          }
          
          items {
            ... on ContentfulFaqItem {
              __typename
              contentful_id
              question
              answer {
                raw
              }
            }
          }
          backgroundColor
        }
        ... on ContentfulHeroFrame {
          __typename
          contentful_id
          title {
            __typename
            contentful_id
            text
          }
          description {
            __typename
            contentful_id
            text {
              raw
            }
          }
          backgroundImage {
            file {
              url
            }
          }
          logos {
            file {
              url
            }
          }
          buttons {
            ... on ContentfulCtaPopUp {
              name
              __typename
              contentful_id
              buttontext
              content {
                ... on ContentfulHubSpotForm {
                  __typename
                  contentful_id
                  formId
                  portalId
                  isOldForm
                }
              }
            }
            ... on ContentfulButton {
              __typename
              contentful_id
              name
              buttonText
              link
              openInNewTab
              variant
            }
          }
        }
        ... on ContentfulPlanCardsContainer {
          __typename
          contentful_id
          mostPopularTitle {
            __typename
            contentful_id
            text
          }
          mostPopularColor
          title {
            __typename
            contentful_id
            text
          }
          isWindowsProduct
          isAppleProduct
          isCardHorizontal
          showComparePlanLink
          cardsAdditionalInfos {
            text {
              raw
            }
          }
          billingPeriods {
            ... on ContentfulCardBillingFormat {
              contentful_id
              selectedDefault
              
              period {
                period
              }
              
              savePercentage {
                percentage
              }

              planCards {
                ... on ContentfulPlanCard {
                  contentful_id
                  productCode
                  mostPopular
                  showContactSales
                  
                  title {
                    __typename
                    contentful_id
                    text
                  }

                  card_billing_format {
                    period {
                      period
                    }
                  }

                  productLogo {
                    title
                    file {
                      url
                    }
                  }

                  description {
                    __typename
                    contentful_id
                    text {
                      raw
                    }
                  }
                  includedFeatures {
                    ... on ContentfulIncludedFeatures {
                      __typename
                      contentful_id
                      title {
                        __typename
                        contentful_id
                        text
                      }
                      features {
                        contentful_id
                        icon
                        iconColor
                        iconSize
                        description {
                          __typename
                          contentful_id
                          text {
                            raw
                          }
                        }
                      }
                    }
                  }
                  included{
                    ... on ContentfulIncludedFeatures {
                      __typename
                      contentful_id
                      title {
                        __typename
                        contentful_id
                        text 
                      }
                      features {
                        contentful_id
                        icon
                        iconColor
                        iconSize
                        description {
                          __typename
                          contentful_id
                          text {
                            raw
                          }
                        }
                      }
                    }
                  }
                  prices {
                    ... on ContentfulPrice {
                      contentful_id
                      price
                      currency {
                        currency
                      }
                      billingPeriod {
                        period
                      }
                      priceNoDiscount
                    }
                  }
                }
              }
            }
          }
        }
        ... on ContentfulCalculatorItem {
          __typename
          contentful_id
          title {
            __typename
            contentful_id
            text
          }
          subtitle {
            __typename
            contentful_id
            text {
              raw
            }
          }
          calculatorType
        }
        ... on ContentfulDownloadSoftwareContainer {
          __typename
          contentful_id
          title {
            __typename
            contentful_id
            text
          }
          subtitle {
            __typename
            contentful_id
            text {
              raw
            }
          }
          categoryTitle {
            __typename
            contentful_id
            text
          }
          softwareItems {
            ... on ContentfulDownloadSoftware {
              __typename
              contentful_id
              productName
              logo {
                file{
                  url
                }
              }
              releaseNotesLink
              showPreviewVersionNumber
              showDownloadVersionNumber
              linksDownload{
                ... on ContentfulDownloadLink {
                  __typename
                  os
                  previewLink
                  downloadLink
                  iosDownloadLink
                }
              }
              isMobileApp
              additionalMessage {
                contentful_id
                text {
                  raw
                }
              }
            }
          }
          softwareAdditionalInfo {
            contentful_id
            text {
              raw
            }
          }
        }  
        ... on ContentfulLegalTermsContainer {
          __typename
          contentful_id
          title {
            __typename
            contentful_id
            text
          }
          subtitle {
            __typename
            contentful_id
            text {
              raw
            }
          }
          categoryTitle {
            __typename
            contentful_id
            text
          }
          legalItems {
            ... on ContentfulStandardTerm {
              __typename
              contentful_id
              icon {
                file{
                  url
                }
              }
              title {
                __typename
                contentful_id
                text
              }
              description {
                __typename
                contentful_id
                text {
                  raw
                }
              }
              showLearnMore
              learnMoreLink
              optionalButtonLabel
              optionalButtonLink
            }
          }
        }  
      }
    }
    site {
      siteMetadata {
        siteUrl
      }
    }
  }
`;